/**
 * Normalize persisted dashboard data into the editor structures that the
 * dashboard layout editor works with.
 */

/**
 * Build the saved breakpoint lookup once so the rest of the normalization code
 * can stay focused on one breakpoint at a time.
 */
function buildSavedBreakpointIndex(savedBreakpoints = []) {
    const index = {};

    for (const breakpoint of savedBreakpoints) {
        if (!breakpoint?.id) {
            continue;
        }

        index[breakpoint.id] = breakpoint;
    }

    return index;
}

/**
 * Normalize one widget positioning entry for one breakpoint.
 */
function createNormalizedWidgetPositioning(widget, widgetIndex, existingPositioning) {
    return {
        widgetId: widget.id || null,
        referenceId: widget.referenceId,
        orderIndex: Number(existingPositioning?.orderIndex ?? widget.orderIndex ?? widgetIndex),
        colspan: Number(existingPositioning?.columnSpan ?? 12),
        offset: Math.max(Number(existingPositioning?.column ?? 1) - 1, 0),
        row: Math.max(Number(existingPositioning?.row ?? 1), 1),
        rowSpan: Math.max(Number(existingPositioning?.rowSpan ?? 1), 1),
        endOfRow: false,
        hidden: Boolean(existingPositioning?.hidden),
        fullWidth: false,
        alignment: null,
    };
}

/**
 * Derived breakpoints should start from the original layout when they do not
 * have their own saved positioning yet. Without that fallback every widget
 * collapses onto the first row with the generic defaults.
 */
function getExistingWidgetPositioning(widget, breakpointId, fallbackBreakpointId = null) {
    if (!Array.isArray(widget.positioning)) {
        return null;
    }

    const directPositioning = widget.positioning.find((positioning) => positioning.breakpointId === breakpointId) || null;

    if (directPositioning) {
        return directPositioning;
    }

    if (!fallbackBreakpointId || fallbackBreakpointId === breakpointId) {
        return null;
    }

    return widget.positioning.find((positioning) => positioning.breakpointId === fallbackBreakpointId) || null;
}

/**
 * Normalize the widget positioning list for one breakpoint.
 */
function createNormalizedBreakpointPositioning(widgets, breakpointId, fallbackBreakpointId = null) {
    const widgetPositioning = [];

    for (let widgetIndex = 0; widgetIndex < widgets.length; widgetIndex += 1) {
        const widget = widgets[widgetIndex];
        const existingPositioning = getExistingWidgetPositioning(
            widget,
            breakpointId,
            fallbackBreakpointId
        );

        widgetPositioning.push(
            createNormalizedWidgetPositioning(widget, widgetIndex, existingPositioning)
        );
    }

    return widgetPositioning;
}

/**
 * Build one normalized breakpoint definition for the editor canvas.
 */
function createNormalizedBreakpoint(
    provider,
    definition,
    breakpointIndex,
    savedBreakpoint,
    supportedBreakpoint,
    widgets,
    fallbackBreakpointId = null
) {
    const breakpointId = definition.id || `breakpoint-${breakpointIndex}`;

    return {
        id: breakpointId,
        title: supportedBreakpoint.title || breakpointId,
        description: supportedBreakpoint.description || '',
        maxWidth: supportedBreakpoint.maxWidth || '100%',
        mediaQuery: supportedBreakpoint.mediaQuery || provider.createDefaultBreakpointMediaQuery(),
        isOriginal: Boolean(savedBreakpoint.isOriginal ?? definition.isOriginal),
        widgetPositioning: createNormalizedBreakpointPositioning(
            widgets,
            breakpointId,
            fallbackBreakpointId
        ),
    };
}

/**
 * Get original breakpoint id.
 *
 * @param {*} breakpoints
 */
function getOriginalBreakpointId(breakpoints = []) {
    for (const breakpoint of breakpoints) {
        if (breakpoint?.isOriginal && breakpoint.id) {
            return breakpoint.id;
        }
    }

    return breakpoints[0]?.id || null;
}

/**
 * Normalize the saved dashboard breakpoints into editor breakpoints.
 */
export function normalizeBreakpointsForEditor(provider, breakpoints, widgets) {
    const savedBreakpoints = Array.isArray(breakpoints) ? breakpoints : [];
    const savedBreakpointIndex = buildSavedBreakpointIndex(savedBreakpoints);
    const sourceWidgets = Array.isArray(widgets) ? widgets : [];
    const originalBreakpointId = getOriginalBreakpointId(savedBreakpoints);
    const desktopDefinition = provider.getSupportedBreakpointDefinitions(savedBreakpoints)
        .find((breakpoint) => breakpoint?.id === 'desktop') || {
            id: 'desktop',
            title: provider.translate('Desktop'),
            isOriginal: true,
        };
    const desktopBreakpoint = createNormalizedBreakpoint(
        provider,
        desktopDefinition,
        0,
        savedBreakpointIndex.desktop || {},
        desktopDefinition,
        sourceWidgets,
        originalBreakpointId
    );

    desktopBreakpoint.isOriginal = true;
    return [desktopBreakpoint];
}

/**
 * Normalize one saved dashboard widget into the block shape the editor expects.
 */
function createNormalizedWidget(provider, widget, widgetIndex, widgetTypeIndex, defaultTabId) {
    const resolvedType = provider.normalizeWidgetType(widget.type || widget.id || '');
    const widgetType = widgetTypeIndex[resolvedType] || {};
    const data = provider.parseJsonData(
        widget.data,
        provider.getWidgetDefaultSettingsValues(widgetType)
    );
    const settings = provider.getWidgetSettingsDefinition(widgetType);

    settings.values = data;

    return {
        id: widget.id || null,
        referenceId: widget.referenceId || provider.getNewReferenceId(),
        name: widget.name || widgetType.name || '',
        nameDefault: widgetType.nameDefault || resolvedType || `Widget ${widgetIndex + 1}`,
        canEditName: widgetType.canEditName ?? true,
        type: resolvedType,
        tabId: widget.tabId || defaultTabId,
        minColspan: widgetType.minColspan || 1,
        areChildrenAllowed: false,
        requiredParent: null,
        settings,
        icon: widgetType.icon || 'dashboard',
        helpText: widgetType.helpText || null,
        blocks: [],
    };
}

/**
 * Normalize the saved dashboard widgets into editor blocks.
 */
export function normalizeWidgetsForEditor(provider, widgets) {
    const normalizedWidgets = [];
    const sourceWidgets = Array.isArray(widgets) ? widgets : [];
    const widgetTypeIndex = provider.getWidgetTypeIndex();
    const defaultTabId = provider.getDefaultDashboardTab().id;

    for (let widgetIndex = 0; widgetIndex < sourceWidgets.length; widgetIndex += 1) {
        const widget = sourceWidgets[widgetIndex];

        normalizedWidgets.push(
            createNormalizedWidget(provider, widget, widgetIndex, widgetTypeIndex, defaultTabId)
        );
    }

    return normalizedWidgets;
}
